import React, { useState } from 'react';
import T from '../styles/theme';
import { Btn } from './UI';
import { useStore } from '../data/store';

/**
 * QuickActionPanel — extracted from ContactRecord's inline quick-action block.
 *
 * Works for both contact-scoped and company-scoped record pages by threading
 * whichever of contactId/companyId/dealId the caller passes into the activity
 * payload. When contactId is present, prefers the contact-scoped store helpers
 * (store.addNote / logCall / logEmail / addTask) which update the contact's
 * lastActivity. When only companyId is present, falls back to store.addActivity
 * with { type, companyId, ... } — the commit-3 activities.company_id column
 * catches these writes.
 *
 * Call handling:
 *   - If onLaunchDialer callback is provided, the call sub-type renders a
 *     single "Open Call Dialer" button that invokes it. This preserves
 *     ContactRecord's existing Twilio dialer behavior identically.
 *   - If onLaunchDialer is NOT provided (CompanyRecord case), the call
 *     sub-type renders a post-call log form (outcome + notes + duration)
 *     which writes via store.logCall against contactId.
 */
export default function QuickActionPanel({
  type,
  contactId,
  companyId,
  dealId,
  contactPhone,
  contactName,
  onLaunchDialer,
  onClose,
  onCreated,
}) {
  const store = useStore();

  // Sub-form state
  const [noteText, setNoteText] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const [taskPri, setTaskPri] = useState("medium");
  const [meetTitle, setMeetTitle] = useState("");
  const [meetDate, setMeetDate] = useState("");
  const [meetNotes, setMeetNotes] = useState("");
  const [callOutcome, setCallOutcome] = useState("Connected");
  const [callDuration, setCallDuration] = useState("5 min");
  const [callNotes, setCallNotes] = useState("");

  const inputS = { width: "100%", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.15)", borderRadius: 4, padding: "8px 10px", color: T.txt, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };
  const labelS = { fontSize: 10, color: T.txt, minWidth: 60, flexShrink: 0 };
  const rowS = { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 };

  const finish = () => { onCreated?.(); onClose?.(); };

  const saveNote = () => {
    if (!noteText.trim()) return;
    const payload = { title: "Note added", notes: noteText };
    if (contactId) store.addNote({ contactId, dealId, ...payload });
    else if (companyId) store.addActivity({ type: "note", companyId, dealId, ...payload });
    finish();
  };

  const saveEmail = () => {
    if (!emailSubject.trim()) return;
    const notes = emailBody || (contactName ? "Sent to " + contactName : "");
    if (contactId) store.logEmail({ contactId, dealId, title: emailSubject, notes });
    else if (companyId) store.addActivity({ type: "email", companyId, dealId, title: emailSubject, notes });
    finish();
  };

  const saveCallLog = () => {
    if (!callNotes.trim() && callOutcome === "Connected") return;
    // CompanyRecord flow arrives here with contactId set via the picker;
    // ContactRecord uses the dialer modal and never reaches this form
    // (onLaunchDialer is always provided). So contactId should always exist.
    if (contactId) {
      store.logCall({ contactId, dealId, title: "Call - " + callOutcome, notes: callNotes || "", duration: callDuration });
    }
    finish();
  };

  const saveTask = () => {
    if (!taskTitle.trim()) return;
    // Tasks table has no company_id (not migrated). Task quick-action is
    // only available when contactId is set (ContactRecord).
    if (!contactId) { onClose?.(); return; }
    store.addTask({ ct: contactId, dealId, title: taskTitle, due: taskDue || "TBD", pri: taskPri });
    finish();
  };

  const saveMeeting = () => {
    if (!meetTitle.trim()) return;
    const notes = (meetDate ? meetDate + " - " : "") + (meetNotes || (contactName ? "Meeting with " + contactName : "Meeting"));
    const payload = { type: "meeting", title: meetTitle, notes, dealId };
    if (contactId) store.addActivity({ ...payload, contactId });
    else if (companyId) store.addActivity({ ...payload, companyId });
    finish();
  };

  return (
    <div style={{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 400, color: "#FAFAFA", marginBottom: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>
        {type === "note" ? "Add Note" : type === "email" ? "Log Email" : type === "call" ? "Log Call" : type === "task" ? "Create Task" : "Log Meeting"}
      </div>

      {type === "note" && (
        <div>
          <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Write a note..." autoFocus
            onKeyDown={e => { if (e.key === "Enter" && e.metaKey) saveNote(); }}
            style={{ ...inputS, height: 80, resize: "none" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <button onClick={onClose} style={{ background: "none", border: "none", color: T.txt, fontSize: 10, cursor: "pointer" }}>Cancel</button>
            <Btn primary style={{ fontSize: 10 }} onClick={saveNote}>Save Note</Btn>
          </div>
        </div>
      )}

      {type === "email" && (
        <div>
          {contactName && (
            <div style={rowS}>
              <span style={labelS}>To:</span>
              <input value={contactName} disabled style={{ ...inputS, opacity: 0.6 }} />
            </div>
          )}
          <div style={rowS}>
            <span style={labelS}>Subject:</span>
            <input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} placeholder="Email subject" autoFocus style={inputS} />
          </div>
          <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} placeholder="Email body..."
            onKeyDown={e => { if (e.key === "Enter" && e.metaKey) saveEmail(); }}
            style={{ ...inputS, height: 80, resize: "none" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <button onClick={onClose} style={{ background: "none", border: "none", color: T.txt, fontSize: 10, cursor: "pointer" }}>Cancel</button>
            <Btn primary style={{ fontSize: 10 }} onClick={saveEmail}>Send Email</Btn>
          </div>
        </div>
      )}

      {type === "call" && onLaunchDialer && (
        <div style={{ textAlign: "center", padding: "12px 0" }}>
          <Btn primary style={{ fontSize: 11 }} onClick={() => { onLaunchDialer(); onClose?.(); }}>
            Open Call Dialer
          </Btn>
        </div>
      )}

      {type === "call" && !onLaunchDialer && (
        <div>
          {contactName && (
            <div style={{ fontSize: 11, color: T.tx3, marginBottom: 10 }}>Logging call with {contactName}{contactPhone ? " (" + contactPhone + ")" : ""}</div>
          )}
          <div style={rowS}>
            <span style={labelS}>Outcome:</span>
            <div style={{ display: "flex", gap: 4, flex: 1 }}>
              {["Connected", "Voicemail", "No Answer", "Busy"].map(o => (
                <button key={o} onClick={() => setCallOutcome(o)} style={{
                  flex: 1, padding: "6px 4px", borderRadius: 4, fontSize: 10, fontWeight: 400, cursor: "pointer",
                  border: callOutcome === o ? "2px solid #2E8B57" : "1px solid " + T.bdr,
                  background: callOutcome === o ? "#2E8B5712" : T.sf,
                  color: callOutcome === o ? "#2E8B57" : T.tx3, fontFamily: "inherit",
                }}>{o}</button>
              ))}
            </div>
          </div>
          <div style={rowS}>
            <span style={labelS}>Duration:</span>
            <input value={callDuration} onChange={e => setCallDuration(e.target.value)} placeholder="e.g. 5 min" style={{ ...inputS, maxWidth: 180 }} />
          </div>
          <textarea value={callNotes} onChange={e => setCallNotes(e.target.value)} placeholder="Call notes..."
            style={{ ...inputS, height: 70, resize: "none" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <button onClick={onClose} style={{ background: "none", border: "none", color: T.txt, fontSize: 10, cursor: "pointer" }}>Cancel</button>
            <Btn primary style={{ fontSize: 10 }} onClick={saveCallLog}>Save Call</Btn>
          </div>
        </div>
      )}

      {type === "task" && (
        <div>
          <div style={rowS}>
            <span style={labelS}>Title:</span>
            <input value={taskTitle} onChange={e => setTaskTitle(e.target.value)} placeholder="Task name..." autoFocus style={inputS} />
          </div>
          <div style={rowS}>
            <span style={labelS}>Due:</span>
            <input type="date" value={taskDue} onChange={e => setTaskDue(e.target.value)} style={{ ...inputS, maxWidth: 180 }} />
          </div>
          <div style={rowS}>
            <span style={labelS}>Priority:</span>
            <div style={{ display: "flex", gap: 4, flex: 1 }}>
              {["low", "medium", "high"].map(p => (
                <button key={p} onClick={() => setTaskPri(p)} style={{
                  flex: 1, padding: "5px 8px", borderRadius: 4, fontSize: 10, fontWeight: 400, cursor: "pointer", textTransform: "capitalize",
                  border: taskPri === p ? "2px solid #4059AD" : "1px solid " + T.bdr,
                  background: taskPri === p ? "rgba(64,89,173,0.15)" : "transparent",
                  color: taskPri === p ? "#FAFAFA" : T.tx3, fontFamily: "inherit",
                }}>{p}</button>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
            <button onClick={onClose} style={{ background: "none", border: "none", color: T.txt, fontSize: 10, cursor: "pointer" }}>Cancel</button>
            <Btn primary style={{ fontSize: 10 }} onClick={saveTask}>Create Task</Btn>
          </div>
        </div>
      )}

      {type === "meeting" && (
        <div>
          <div style={rowS}>
            <span style={labelS}>Title:</span>
            <input value={meetTitle} onChange={e => setMeetTitle(e.target.value)} placeholder="Meeting title..." autoFocus style={inputS} />
          </div>
          <div style={rowS}>
            <span style={labelS}>Date:</span>
            <input type="datetime-local" value={meetDate} onChange={e => setMeetDate(e.target.value)} style={{ ...inputS, maxWidth: 220 }} />
          </div>
          <textarea value={meetNotes} onChange={e => setMeetNotes(e.target.value)} placeholder="Meeting notes, attendees, agenda..."
            onKeyDown={e => { if (e.key === "Enter" && e.metaKey) saveMeeting(); }}
            style={{ ...inputS, height: 60, resize: "none" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <button onClick={onClose} style={{ background: "none", border: "none", color: T.txt, fontSize: 10, cursor: "pointer" }}>Cancel</button>
            <Btn primary style={{ fontSize: 10 }} onClick={saveMeeting}>Log Meeting</Btn>
          </div>
        </div>
      )}
    </div>
  );
}
